import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { formatKpiValue, isNoData, isOffTrack, isOnTrack, KpiPerformance } from '../../util/kpiPerformance';
import { kpiColor, SparklineSVG } from './PacingSparkline';

// KpiPerformanceChips renders one contract's KPI Performance cell (ADOPS-2705):
// off-track KPIs flagged with name/value/color, an overflow chip when more than
// three are off-track (worst two shown), a No-Data chip, and an On-Track chip.
// Adapted from the media-labs prototype's KpiChips, reusing OneDSP's SparklineSVG
// and the kpi_options-driven value formatting. Color uses kpiColor (one-directional:
// at/above goal stays green), not the symmetric pacing continuousColor.

// Ticket: collapse only when MORE than three KPIs are off-track; then show the
// worst two (by biggest gap from goal, i.e. lowest % of goal) plus an overflow chip.
const OVERFLOW_THRESHOLD = 3;
const VISIBLE_WHEN_OVERFLOW = 2;

// A KPI cell shows at most 5 chips: ≤3 off-track (the collapsing above caps it —
// >3 collapses to 2 + an overflow chip), plus one No-Data and one On-Track
// summary chip. Those fit in two wrapped rows at any sane column width, so cap the
// wrap at two rows: a narrowed column then clips the extra row cleanly (each
// visible chip stays whole) instead of sprawling to 3–5 lines and overflowing the
// grid row. Widen the column to reveal the rest. Chip text is 0.74rem at the MUI
// body line-height (1.5); two of those plus the 2px rowGap is the cap.
const MAX_CHIP_ROWS = 2;
const CHIP_FONT_REM = 0.74;
const CHIP_LINE_HEIGHT = 1.5;
const CHIP_ROW_GAP_PX = 2; // theme spacing 0.25 → 2px (one gap between the two rows)
const CHIP_AREA_MAX_HEIGHT = `calc(${CHIP_FONT_REM}rem * ${CHIP_LINE_HEIGHT} * ${MAX_CHIP_ROWS} + ${CHIP_ROW_GAP_PX}px)`;

function KpiTooltipBody({ kpi }: { kpi: KpiPerformance }) {
  const showSparkline =
    kpi.temporal === 'daily' &&
    !!kpi.dailyValues &&
    kpi.dailyValues.length >= 2 &&
    kpi.goal != null &&
    kpi.goal !== 0; // sparkline divides by goal — avoid Infinity/NaN

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25, minWidth: 170 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, lineHeight: 1.2 }}>{kpi.name}</Typography>
        {kpi.pctOfGoal != null && (
          <Typography sx={{ fontSize: '0.68rem', opacity: 0.8 }}>{kpi.pctOfGoal.toFixed(0)}% of goal</Typography>
        )}
      </Box>
      {/* Provider directly under the KPI name (ADOPS-2797); "Not Specified" when absent. */}
      <Typography sx={{ fontSize: '0.6rem', opacity: 0.8, mt: -0.75, lineHeight: 1.1 }}>
        Provider: {kpi.provider ?? 'Not Specified'}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 1.5, rowGap: 0.25 }}>
        <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Goal</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'right' }}>
          {formatKpiValue(kpi.goal, kpi.format)}
        </span>
        <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>Performance</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, textAlign: 'right' }}>
          {formatKpiValue(kpi.value, kpi.format)}
        </span>
      </Box>
      {showSparkline && (
        <Box sx={{ mt: 0.25 }}>
          <SparklineSVG
            values={kpi.dailyValues!.map((v) => v / (kpi.goal as number))}
            width={160}
            height={26}
            colorFn={kpiColor}
          />
        </Box>
      )}
    </Box>
  );
}

function OffTrackIndicator({ kpi }: { kpi: KpiPerformance }) {
  const color = kpiColor((kpi.pctOfGoal as number) / 100);

  return (
    <Tooltip title={<KpiTooltipBody kpi={kpi} />} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-offtrack"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, lineHeight: 1.1, cursor: 'help' }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.74rem', color, fontWeight: 500, whiteSpace: 'nowrap' }}>{kpi.name}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatKpiValue(kpi.value, kpi.format)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function OverflowOffTrackChip({ kpis }: { kpis: KpiPerformance[] }) {
  const worst = Math.min(...kpis.map((k) => k.pctOfGoal as number));
  const color = kpiColor(worst / 100);
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>
        Also off-track ({kpis.length})
      </Typography>
      {kpis.map((k) => (
        <KpiTooltipBody key={k.name} kpi={k} />
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-overflow"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'help', lineHeight: 1.1 }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.72rem', color, fontWeight: 600, whiteSpace: 'nowrap' }}>
          +{kpis.length} off-track
        </Typography>
      </Box>
    </Tooltip>
  );
}

function NoDataChip({ kpis }: { kpis: KpiPerformance[] }) {
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>No data ({kpis.length})</Typography>
      {kpis.map((k) => (
        <Box key={k.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
          <span style={{ fontSize: '0.7rem' }}>{k.name}</span>
          <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
            {k.goal != null ? `goal ${formatKpiValue(k.goal, k.format)}` : 'no goal'}
          </span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-nodata"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'help', lineHeight: 1.1 }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
          +{kpis.length} No-Data
        </Typography>
      </Box>
    </Tooltip>
  );
}

function OnTrackChip({ kpis }: { kpis: KpiPerformance[] }) {
  const tooltip = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, opacity: 0.85 }}>On track ({kpis.length})</Typography>
      {kpis.map((k) => (
        <KpiTooltipBody key={k.name} kpi={k} />
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        tabIndex={0}
        data-testid="kpi-ontrack"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, lineHeight: 1.1, cursor: 'help' }}
      >
        <CheckCircleIcon sx={{ fontSize: 12, color: '#2E7D32' }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {kpis.length} On-Track
        </Typography>
      </Box>
    </Tooltip>
  );
}

export interface KpiPerformanceChipsProps {
  kpis: KpiPerformance[];
}

export default function KpiPerformanceChips({ kpis }: KpiPerformanceChipsProps): JSX.Element | null {
  if (!kpis || kpis.length === 0) return null;

  const offTrack = kpis.filter(isOffTrack).sort((a, b) => (a.pctOfGoal as number) - (b.pctOfGoal as number));
  const onTrack = kpis.filter(isOnTrack);
  const noData = kpis.filter(isNoData);

  const overflow = offTrack.length > OVERFLOW_THRESHOLD;
  const visibleOffTrack = overflow ? offTrack.slice(0, VISIBLE_WHEN_OVERFLOW) : offTrack;
  const hiddenOffTrack = overflow ? offTrack.slice(VISIBLE_WHEN_OVERFLOW) : [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'center',
        alignContent: 'flex-start', // pack wrapped rows from the top so the FIRST two are the visible ones
        maxHeight: CHIP_AREA_MAX_HEIGHT,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {visibleOffTrack.map((kpi) => (
        <OffTrackIndicator key={kpi.name} kpi={kpi} />
      ))}
      {hiddenOffTrack.length > 0 && <OverflowOffTrackChip kpis={hiddenOffTrack} />}
      {noData.length > 0 && <NoDataChip kpis={noData} />}
      {onTrack.length > 0 && <OnTrackChip kpis={onTrack} />}
    </Box>
  );
}
